import type React from "react";
import { useState, useEffect, useRef, useMemo } from "react";
import {
  Search,
  Play,
  Pause,
  Volume2,
  Maximize,
  SkipBack,
  SkipForward,
  Share2,
  ChevronUp,
  ChevronDown,
  ArrowLeft,
  FileText,
  ClipboardCheck,
  GraduationCap,
  MessageSquare,
  AlertCircle,
  Download,
  Eye,
  CheckCircle,
  XCircle,
  X,
  Video,
  Image as ImageIcon,
  Code,
  Circle,
  CheckCircle2,
  Clock,
  Sparkles,
  Brain,
  PenLine,
} from "lucide-react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { ApiService } from "../../services/api";
import { openAITeacherForGain } from "../../utils/aiTeacher";
import { useModal } from "../../hooks/useModal";
import checkPermission from "../../utils/permission";
import LessonQAView from "./components/interactions/LessonQAView";
import ContentViewer from "./components/content/ContentViewer";
import LessonHomeworkView from "./components/homework/LessonHomeworkView";

export const VideoPlayerPage = () => {
  const { lessonId } = useParams();
  const navigate = useNavigate();
  const { showToast } = useModal();
  const location = useLocation();
  const [lesson, setLesson] = useState<any>(null);
  const [currentSection, setCurrentSection] = useState<any>(null);
  const [currentContent, setCurrentContent] = useState<any>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(1440);
  // Varsayılan sekme Eksikler: öğrenci derse dönünce ilk göreceği şey açık eksikleri
  // (yoksa "eksiğin yok" olumlu boş durumu). Notlar/Yorumlar sekmeleri kaldırıldı —
  // sahada kullanılmadıkları görüldü.
  const [activeContentTab, setActiveContentTab] = useState("deficiencies");
  const [showControls, setShowControls] = useState(true);
  const [volume, setVolume] = useState(1);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [expandedSections, setExpandedSections] = useState<Record<number, boolean>>({});
  const [isSidebarOpen, setIsSidebarOpen] = useState(() => {
    return window.innerWidth >= 768;
  });
  const [sectionGains, setSectionGains] = useState<Record<string, any[]>>({});
  // Ders genelindeki açık eksikler; null = henüz yüklenmedi (boş durumla karışmasın)
  const [lessonGains, setLessonGains] = useState<any[] | null>(null);
  // Sunucu planı kurulurken düğmeyi kilitler (hangi bölüm için başlatıldıysa onun id'si)
  const [tutorStarting, setTutorStarting] = useState<string | null>(null);
  const canQaList = checkPermission("lesson_qa_list");
  // İçerik yöneten roller (öğretmen/admin) geri tuşuyla detay/yönetim sayfasına döner;
  // öğrenci doğrudan ders listesine döner (detay sayfası öğrenci akışından çıkarıldı).
  const canManageLesson = checkPermission("lesson_update") || checkPermission("lesson_add");

  const sectionRefs = useRef<Record<number, HTMLDivElement | null>>({});

  const memoizedContent = useMemo(() => {
    if (!currentContent) return null;
    return {
      ...currentContent,
      lessonId,
      sectionId: currentSection?.id,
    };
  }, [currentContent, lessonId, currentSection?.id]);

  const scrollToSection = (sectionIndex: number) => {
    const sectionElement = sectionRefs.current[sectionIndex];
    if (sectionElement) {
      sectionElement.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  };

  // TEK MERKEZ: ders ekranındaki eksikler de Eksiklerim/Koçum ile AYNI yoldan gider.
  // Planı sunucu kurar — bu föydeki TÜM açık eksikler, istenen kazanım önce, kalanlar
  // föy sırasıyla. Öğrenci sayfadan çıkmaz; panel bulunduğu yerde açılır.
  const openTutorForGains = async (gains: any[], sectionId: string) => {
    const firstGain = (gains || [])[0];
    if (!firstGain?.id) return;

    // Eksikler ders geneli listelendiği için grup, o an açık olmayan bir bölüme ait
    // olabilir — bağlam (başlık, içerik, pdf) ekrandakinden değil, grubun kendi
    // bölümünden kurulur; bölüm ekrandakiyle aynıysa açık içerik bağlam olarak gider.
    const targetSection = (lesson?.sections || []).find((s: any) => s.id === sectionId);
    const sectionMatchesCurrent = currentSection?.id === sectionId;

    setTutorStarting(sectionId);
    try {
      await openAITeacherForGain(firstGain.id, {
        gainId: firstGain.id,
        gainText: firstGain.gainText || "",
        contentId: sectionMatchesCurrent ? currentContent?.id || "" : "",
        contentTitle: sectionMatchesCurrent ? currentContent?.title || "" : "",
        topicId: sectionId,
        topicTitle: targetSection?.title || currentSection?.title || "",
        lessonId: lessonId || "",
        lessonTitle: lesson?.title || "",
        startTime: firstGain.startTime || "",
        endTime: firstGain.endTime || "",
        pdfUrl:
          sectionMatchesCurrent &&
          ["FILE", "PDF"].includes(String(currentContent?.type || "").toUpperCase())
            ? currentContent?.media?.url || ""
            : "",
        source: "deficiency",
      }, { onCompleted: (message) => showToast(message, "success") });
    } finally {
      setTutorStarting(null);
    }
  };

  const handleTrackingUpdate = async (contentId: string) => {
    if (!lessonId) return;
    try {
      const res = await ApiService.getLessonPreview(lessonId);
      setLesson(res);
      
      let targetSection: any = null;
      let isLastTestInSection = false;
      
      for (const section of (res?.sections || [])) {
        const content = section?.contents?.find((c: any) => c.id === contentId);
        if (content) {
          targetSection = section;
          const tests = section?.contents?.filter((c: any) => 
            c.type?.toUpperCase() === "TEST" || c.type?.toUpperCase() === "EXAM"
          ) || [];
          if (tests.length > 0) {
            const lastTest = tests[tests.length - 1];
            isLastTestInSection = lastTest.id === contentId;
          }
          break;
        }
      }
      
      const all = await loadLessonGains();
      const targetSectionGains = all.filter((g: any) => g.topic?.id === targetSection?.id);

      // Bölümün son testi bitti ve eksik çıktıysa AI Öğretmen kendiliğinden açılır.
      // Eskiden burada DeficiencyTutorPanel açılıyordu — öğrenci farkında olmadan
      // ayrı bir motora düşüyordu; artık herkesle aynı yoldan (teach/start) gider.
      if (targetSection && isLastTestInSection && isSectionTestsCompleted(targetSection) && targetSectionGains.length > 0) {
        void openTutorForGains(targetSectionGains, targetSection.id);
      }
    } catch (error) {
      // Lesson verisi güncellenemedi
    }
  };

  const isSectionTestsCompleted = (section: any): boolean => {
    const tests = section?.contents?.filter((c: any) => 
      c.type?.toUpperCase() === "TEST" || c.type?.toUpperCase() === "EXAM"
    ) || [];
    if (tests.length === 0) return false;
    return tests.every((test: any) => test.tracking?.status === "COMPLETED");
  };

  // Dersin TÜM açık eksikleri tek çağrıda gelir; kenar çubuğundaki bölüm sayaçları da
  // aynı yanıttan türetilir (eskiden bölüm başına aynı istek tekrar tekrar atılıyordu).
  const loadLessonGains = async (): Promise<any[]> => {
    try {
      const response = await ApiService.getUserGains({
        lessonId: lessonId,
        isResolved: false,
        // Varsayılan 20; ders geneli liste ve sayaç kırpılmasın diye açıkça yükseltildi.
        limit: 200,
      });
      const all: any[] = response?.data || [];
      setLessonGains(all);
      const bySection: Record<string, any[]> = {};
      all.forEach((g: any) => {
        const sid = g.topic?.id;
        if (!sid) return;
        (bySection[sid] = bySection[sid] || []).push(g);
      });
      setSectionGains(bySection);
      return all;
    } catch (err) {
      setLessonGains([]);
      return [];
    }
  };

  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;
    if (isPlaying && currentTime < duration) {
      interval = setInterval(() => {
        setCurrentTime((prevTime) => {
          if (prevTime >= duration) {
            setIsPlaying(false);
            return duration;
          }
          return prevTime + 1;
        });
      }, 1000);
    } else if (!isPlaying) {
      if (interval) {
        clearInterval(interval);
      }
    }
    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [isPlaying, currentTime, duration]);
  useEffect(() => {
    let timeout: NodeJS.Timeout | null = null;
    if (isPlaying && showControls) {
      timeout = setTimeout(() => {
        setShowControls(false);
      }, 3000);
    }
    return () => {
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, [isPlaying, showControls]);

  const getContentTypeBadgeClass = (type: string) => {
    switch ((type || "").toUpperCase()) {
      case "VIDEO":
        return "bg-purple-100 text-purple-700";
      case "AUDIO":
        return "bg-blue-100 text-blue-700";
      case "IMAGE":
        return "bg-green-100 text-green-700";
      case "TEXT":
        return "bg-gray-100 text-gray-700";
      case "FILE":
      case "PDF":
        return "bg-red-100 text-red-700";
      case "CODE":
        return "bg-yellow-100 text-yellow-700";
      case "TEST":
      case "EXAM":
        return "bg-emerald-100 text-emerald-700";
      case "WORKSHEET":
        return "bg-indigo-100 text-indigo-700";
      default:
        return "bg-slate-100 text-slate-700";
    }
  };

  const getContentTypeIcon = (type: string) => {
    switch (type?.toUpperCase()) {
      case "VIDEO":
        return <Video className="w-4 h-4" />;
      case "AUDIO":
        return <Volume2 className="w-4 h-4" />;
      case "IMAGE":
        return <ImageIcon className="w-4 h-4" />;
      case "TEXT":
        return <FileText className="w-4 h-4" />;
      case "FILE":
      case "PDF":
        return <FileText className="w-4 h-4" />;
      case "CODE":
        return <Code className="w-4 h-4" />;
      case "TEST":
      case "EXAM":
        return <ClipboardCheck className="w-4 h-4" />;
      case "WORKSHEET":
        return <PenLine className="w-4 h-4 text-indigo-600" />;
      default:
        return <FileText className="w-4 h-4" />;
    }
  };

  const getProgressIcon = (content: any) => {
    if (content.tracking == null) {
      return <Circle className="w-4 h-4 text-gray-400" />;
    }
    if (content.tracking.status === "COMPLETED") {
      return <CheckCircle2 className="w-4 h-4 text-green-600" />;
    }
    if (content.tracking.status === "VIEWED") {
      return <Clock className="w-4 h-4 text-yellow-600" />;
    }
    return <Circle className="w-4 h-4 text-gray-400" />;
  };

  const toggleSection = (index: number) => {
    setExpandedSections((prev) => {
      const isCurrentlyExpanded = prev[index as keyof typeof prev];
      const newState = {
        ...prev,
        [index]: !isCurrentlyExpanded,
      };

      if (!isCurrentlyExpanded) {
        setTimeout(() => scrollToSection(index), 100);
      }

      return newState;
    });
  };

  useEffect(() => {
    const fetchLesson = async () => {
      if (!lessonId) return;
      const res = await ApiService.getLessonPreview(lessonId);
      setLesson(res);
      // Eksikler varsayılan sekme olduğu için ders geneli eksikler açılışta yüklenir
      void loadLessonGains();
    };
    fetchLesson();
  }, [lessonId]);

  // AI Öğretmen bir kazanımı kapattığında listeyi tazele (panel açıkken de olur —
  // öğrenci sohbete devam ederken arkadaki sayaç eskimesin).
  useEffect(() => {
    const onGainResolved = () => {
      void loadLessonGains();
    };
    window.addEventListener("aiTeacherGainResolved", onGainResolved);
    return () => window.removeEventListener("aiTeacherGainResolved", onGainResolved);
  }, [lessonId]);

  const selectContentById = (contentId: string) => {
    if (!lesson?.sections) return;
    for (let i = 0; i < lesson.sections.length; i++) {
      const sec = lesson.sections[i];
      const found = (sec.contents || []).find((c: any) => c.id === contentId);
      if (found) {
        setCurrentSection(sec);
        setCurrentContent(found);
        setExpandedSections({ [i]: true });
        setTimeout(() => scrollToSection(i), 100);
        return;
      }
    }
  };

  const goToContent = (sectionId: string, contentId: string) => {
    navigate(`/lessons/play/${lessonId}?contentId=${contentId}`);
    if (lesson?.sections) {
      const sectionIndex = lesson.sections.findIndex((s: any) => s.id === sectionId);
      const sec = lesson.sections[sectionIndex];
      const cnt = sec?.contents?.find((c: any) => c.id === contentId);
      if (sec) setCurrentSection(sec);
      if (cnt) {
        setCurrentContent(cnt);
      }
      if (sectionIndex !== -1) {
        setExpandedSections({ [sectionIndex]: true });
        setTimeout(() => scrollToSection(sectionIndex), 100);
      }
    }
    setActiveContentTab((prev) => prev);
  };

  useEffect(() => {
    if (!lesson) return;
    const params = new URLSearchParams(location.search);
    const cid = params.get("contentId");
    if (cid) {
      if (currentContent?.id !== cid) selectContentById(cid);
    } else if (!currentContent) {
      // Kaldığı yerden devam: önce bu cihazda son açılan içerik (localStorage),
      // yoksa sunucu takibine göre ilk tamamlanmamış içerik, o da yoksa ilk içerik.
      const saved = (() => {
        try {
          return JSON.parse(localStorage.getItem(`lessonProgress_${lessonId}`) || "null");
        } catch {
          return null;
        }
      })();
      const savedExists =
        saved?.contentId &&
        (lesson.sections || []).some((s: any) => (s.contents || []).some((c: any) => c.id === saved.contentId));
      if (savedExists) {
        selectContentById(saved.contentId);
        return;
      }

      const flat: Array<{ section: any; content: any; sectionIndex: number }> = [];
      (lesson.sections || []).forEach((section: any, sectionIndex: number) =>
        (section.contents || []).forEach((content: any) => flat.push({ section, content, sectionIndex }))
      );
      const nextUp =
        flat.find((f) => String(f.content?.tracking?.status || "").toUpperCase() !== "COMPLETED") || flat[0];
      if (nextUp) {
        setCurrentSection(nextUp.section);
        setCurrentContent(nextUp.content);
        setExpandedSections({ [nextUp.sectionIndex]: true });
        setTimeout(() => scrollToSection(nextUp.sectionIndex), 100);
      } else if (lesson.sections?.[0]) {
        setCurrentSection(lesson.sections[0]);
        setExpandedSections({ 0: true });
      }
    }
  }, [lesson, location.search]);

  useEffect(() => {
    if (currentContent && currentSection && lesson) {
      localStorage.setItem(
        `lessonProgress_${lessonId}`,
        JSON.stringify({
          sectionId: currentSection.id,
          sectionTitle: currentSection.title,
          contentId: currentContent.id,
          contentTitle: currentContent.title,
          contentType: currentContent.type,
          timestamp: new Date().toISOString(),
        })
      );
    }
  }, [currentContent, currentSection, lessonId, lesson]);

  // Eksikler bu dersin bölüm sırasına göre gruplanır. Bölümü bu derste OLMAYAN eksik
  // gösterilmez: aksi halde başka bir dersin eksiği bu dersin listesine düşerdi.
  const deficiencyGroups = (lesson?.sections || [])
    .map((section: any) => ({ section, gains: sectionGains[section.id] || [] }))
    .filter((group: any) => group.gains.length > 0);

  // Sayaçlar listelenenle birebir aynı olmalı (lessonGains ham yanıt, gruplar süzülmüş).
  const deficiencyCount = deficiencyGroups.reduce((sum: number, g: any) => sum + g.gains.length, 0);

  return (
    <div className="h-[88vh] bg-gray-900 flex flex-col overflow-hidden">
      <div className="bg-white border-b border-gray-200 px-4 sm:px-6 py-4">
        <div className="flex flex-wrap items-center gap-3 sm:gap-4">
          <button
            onClick={() => navigate(canManageLesson ? `/lessons/detail/${lessonId}` : "/lessons")}
            className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 transition-colors">
            <ArrowLeft className="w-5 h-5" />
            <span className="font-medium">Geri</span>
          </button>

          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-gray-600">
            <span className="text-gray-400">|</span>
            <button
              onClick={() => navigate(canManageLesson ? `/lessons/detail/${lessonId}` : "/lessons")}
              className="font-medium text-gray-900 hover:text-purple-600 transition-colors cursor-pointer">
              {lesson?.title}
            </button>
            {/* Bölüm adı telefonda gizli: kırıntı yolu üç satıra sarıp ~110px yiyor,
                oysa bölüm bilgisi zaten kenar çubuğunda ve içerik başlığında var. */}
            {currentSection && (
              <>
                <span className="hidden sm:inline text-gray-400">›</span>
                <span className="hidden sm:inline text-gray-700">{currentSection.title}</span>
              </>
            )}
            {currentContent && (
              <>
                <span className="text-gray-400">›</span>
                <span className="text-purple-600 font-medium">{currentContent.title}</span>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden flex-col md:flex-row">
        <div className="flex-1 overflow-y-auto min-h-0">
          <div
            className="relative w-full"
            onClick={() => currentContent?.type === "VIDEO" && setShowControls(!showControls)}
            onMouseMove={() => currentContent?.type === "VIDEO" && setShowControls(true)}>
            {memoizedContent && (
              <ContentViewer content={memoizedContent} onTrackingUpdate={handleTrackingUpdate} isParentSidebarOpen={isSidebarOpen} />
            )}
          </div>

          <div className="bg-white border-t border-gray-200">
            <div className="max-w-7xl mx-auto px-4 sm:px-6">
              <div className="flex space-x-4 sm:space-x-8 overflow-x-auto scrollbar-hide -mx-4 sm:mx-0 px-4 sm:px-0 whitespace-nowrap">
                {[
                  { id: "deficiencies", label: "Eksiklerim", icon: AlertCircle, badge: deficiencyCount },
                  { id: "assignments", label: "Ödevler", icon: GraduationCap, badge: 0 },
                  ...(lesson?.isQa && canQaList ? [{ id: "qa", label: "Soru-Cevap", icon: MessageSquare, badge: 0 }] : []),
                ].map((tab) => {
                  const Icon = tab.icon;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveContentTab(tab.id)}
                      className={`py-4 px-2 border-b-2 font-medium text-sm transition-colors ${
                        activeContentTab === tab.id
                          ? "border-purple-500 text-purple-600"
                          : "border-transparent text-gray-500 hover:text-gray-700"
                      }`}>
                      <div className="flex items-center gap-2">
                        <Icon className="w-4 h-4" />
                        {tab.label}
                        {tab.badge > 0 && (
                          <span className="min-w-[20px] h-5 px-1.5 rounded-full bg-purple-100 text-purple-700 text-xs font-bold flex items-center justify-center">
                            {tab.badge}
                          </span>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="bg-gray-50">
            {activeContentTab === "qa" && lesson?.isQa && canQaList && (
              <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
                <div className="bg-white rounded-lg p-4 sm:p-6 shadow-sm">
                  <LessonQAView lessonId={lessonId!} sections={lesson?.sections || []} defaultSectionId={currentSection?.id} />
                </div>
              </div>
            )}

            {activeContentTab === "deficiencies" && (
              <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
                <div className="bg-white rounded-lg p-4 sm:p-6 shadow-sm">

                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4 mb-6">
                    <div className="flex flex-wrap items-center gap-2">
                      <AlertCircle className="w-5 h-5 text-purple-600 flex-shrink-0" />
                      <h2 className="text-lg sm:text-xl font-bold text-gray-900 flex-shrink-0">Eksiklerim</h2>
                      {lesson?.title && (
                        <span className="text-xs px-2 py-1 rounded-full bg-purple-50 text-purple-700 border border-purple-100 hidden sm:inline-block">
                          {lesson.title} — dersin tamamı
                        </span>
                      )}
                    </div>
                    <button
                      onClick={() => navigate("/lessons?tab=deficiencies")}
                      className="text-xs sm:text-sm text-purple-600 hover:text-purple-700 font-medium hover:underline flex items-center gap-1 flex-shrink-0">
                      <span className="hidden sm:inline">Tüm Derslerdeki Eksikler</span>
                      <span className="sm:hidden">Tümünü Gör</span>
                      <ArrowLeft className="w-3.5 h-3.5 sm:w-4 sm:h-4 rotate-180" />
                    </button>
                  </div>

                  {lessonGains === null ? (
                    <div className="bg-gray-50 rounded-lg p-8 text-center text-sm text-gray-500">
                      Eksikler yükleniyor...
                    </div>
                  ) : deficiencyGroups.length > 0 ? (
                    <div className="space-y-6">
                      <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-4 sm:p-6 border border-purple-100">
                        <div className="flex items-center gap-4 text-left">
                           <div className="w-12 h-12 sm:w-16 sm:h-16 bg-white rounded-full shadow-sm flex items-center justify-center flex-shrink-0">
                              <Sparkles className="w-6 h-6 sm:w-8 sm:h-8 text-purple-600" />
                           </div>
                           <div className="flex-1 min-w-0">
                              <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-1">AI ile Eksiklerini Kapat</h3>
                              <p className="text-sm sm:text-base text-gray-600">
                                Bu derste toplam <strong>{deficiencyCount}</strong> açık eksiğin var.
                                Bölümüne git, AI Öğretmen hepsini sırayla, bu sayfadan ayrılmadan anlatsın.
                              </p>
                           </div>
                        </div>
                      </div>

                      {deficiencyGroups.map(({ section, gains }: any) => (
                        <div key={section.id} className="border border-gray-200 rounded-xl overflow-hidden">
                          <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 px-4 py-3 bg-gray-50 border-b border-gray-200">
                            <div className="flex-1 min-w-0">
                              <p className="font-semibold text-gray-900 truncate">{section.title}</p>
                              <p className="text-xs text-gray-500">{gains.length} açık eksik</p>
                            </div>
                            <button
                              onClick={() => void openTutorForGains(gains, section.id)}
                              disabled={tutorStarting !== null}
                              className="px-4 py-2.5 bg-purple-600 text-white rounded-lg font-semibold text-sm shadow hover:bg-purple-700 transition-colors flex items-center justify-center gap-2 flex-shrink-0 disabled:opacity-70 disabled:cursor-not-allowed">
                              {tutorStarting === section.id ? (
                                <>
                                  <Brain className="w-4 h-4 animate-pulse" />
                                  Ders planı hazırlanıyor...
                                </>
                              ) : (
                                <>
                                  <Sparkles className="w-4 h-4" />
                                  Eksiklerini Gider
                                </>
                              )}
                            </button>
                          </div>
                          <div className="divide-y divide-gray-100">
                            {gains.map((gain: any, index: number) => (
                              <div key={gain.id} className="px-4 py-3 flex items-center gap-3">
                                <div className="w-8 h-8 rounded-full bg-purple-50 flex items-center justify-center font-bold text-purple-600 text-sm flex-shrink-0">
                                  {index + 1}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium text-gray-900 truncate">{gain.gainText || "İsimsiz Kazanım"}</p>
                                </div>
                                {gain.createdAt && (
                                  <span className="hidden sm:flex items-center gap-1 text-xs text-gray-400 flex-shrink-0">
                                    <Clock className="w-3 h-3" />
                                    {new Date(gain.createdAt).toLocaleDateString("tr-TR")}
                                  </span>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="bg-gray-50 rounded-lg p-6 sm:p-8 text-center border-2 border-dashed border-gray-200">
                      <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-sm">
                         <CheckCircle2 className="w-8 h-8 text-green-500" />
                      </div>
                      <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-2">Harika Gidiyorsun!</h3>
                      <p className="text-sm sm:text-base text-gray-500 mb-4 sm:mb-6">
                        Bu derste tespit edilen açık bir eksiğin bulunmuyor.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {activeContentTab === "assignments" && (
              <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
                <div className="bg-white rounded-lg p-4 sm:p-6 shadow-sm">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4 mb-4">
                    <div className="flex flex-wrap items-center gap-2">
                      <GraduationCap className="w-5 h-5 text-purple-600 flex-shrink-0" />
                      <h2 className="text-lg sm:text-xl font-bold text-gray-900 flex-shrink-0">Ödevler</h2>
                      {currentSection && (
                        <span className="text-xs px-2 py-1 rounded-full bg-purple-50 text-purple-700 border border-purple-100">
                          {currentSection.title}
                        </span>
                      )}
                    </div>
                    <button
                      onClick={() => navigate("/lessons?tab=homeworks")}
                      className="text-xs sm:text-sm text-purple-600 hover:text-purple-700 font-medium hover:underline flex items-center gap-1 flex-shrink-0">
                      <span className="hidden sm:inline">Tüm Ödevleri Görüntüle</span>
                      <span className="sm:hidden">Tümünü Gör</span>
                      <ArrowLeft className="w-3.5 h-3.5 sm:w-4 sm:h-4 rotate-180" />
                    </button>
                  </div>
                  <LessonHomeworkView lessonId={lessonId!} sectionId={currentSection?.id} />
                </div>
              </div>
            )}
          </div>
        </div>
        {isSidebarOpen && (
          <div className="w-full md:w-80 bg-white border-l md:border-l border-t md:border-t-0 border-gray-200 flex flex-col overflow-hidden max-h-[50vh] md:max-h-none">
            <div className="p-4 border-b flex items-center justify-between">
              <h2 className="font-semibold text-gray-900">Ders İçeriği</h2>
              <button
                onClick={() => setIsSidebarOpen(false)}
                className="p-1 hover:bg-gray-100 rounded-full transition-colors"
                title="Sidebar'ı Kapat">
                <X className="w-4 h-4 text-gray-500" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto">
              <div className="space-y-1">
                {lesson?.sections?.map((section: any, index: number) => (
                  <div
                    key={index}
                    ref={(el) => {
                      sectionRefs.current[index] = el;
                    }}>
                    <button
                      onClick={() => toggleSection(index)}
                      className="w-full flex items-center justify-between p-3 text-left hover:bg-gray-50 transition-colors">
                      <div className="flex flex-col items-start w-full">
                        <div className="flex items-center w-full">
                          {expandedSections[index as keyof typeof expandedSections] ? (
                            <ChevronUp className="w-4 h-4 mr-2" />
                          ) : (
                            <ChevronDown className="w-4 h-4 mr-2" />
                          )}
                          <span className="font-medium text-sm">
                            Bölüm {index + 1}: {section.title}
                          </span>
                        </div>
                        <div className="ml-6 mt-2">
                          {(() => {
                            const contentTypes: Record<
                              string,
                              {
                                total: number;
                                completed: number;
                                icon: string;
                                color: string;
                              }
                            > = {};

                            section?.contents?.forEach((content: any) => {
                              const type = content.type?.toUpperCase() || "TEXT";
                              const typeName =
                                type === "VIDEO"
                                  ? "video"
                                  : type === "AUDIO"
                                  ? "ses"
                                  : type === "IMAGE"
                                  ? "resim"
                                  : type === "TEXT"
                                  ? "metin"
                                  : type === "FILE" || type === "PDF"
                                  ? "dosya"
                                  : type === "CODE"
                                  ? "kod"
                                  : type === "TEST" || type === "EXAM"
                                  ? "test"
                                  : "içerik";

                              const icon =
                                type === "VIDEO"
                                  ? "🎥"
                                  : type === "AUDIO"
                                  ? "🎵"
                                  : type === "IMAGE"
                                  ? "🖼️"
                                  : type === "TEXT"
                                  ? "📝"
                                  : type === "FILE" || type === "PDF"
                                  ? "📁"
                                  : type === "CODE"
                                  ? "💻"
                                  : type === "TEST" || type === "EXAM"
                                  ? "📋"
                                  : "📄";

                              const color =
                                type === "VIDEO"
                                  ? "bg-purple-100 text-purple-700"
                                  : type === "AUDIO"
                                  ? "bg-blue-100 text-blue-700"
                                  : type === "IMAGE"
                                  ? "bg-green-100 text-green-700"
                                  : type === "TEXT"
                                  ? "bg-gray-100 text-gray-700"
                                  : type === "FILE" || type === "PDF"
                                  ? "bg-red-100 text-red-700"
                                  : type === "CODE"
                                  ? "bg-yellow-100 text-yellow-700"
                                  : type === "TEST" || type === "EXAM"
                                  ? "bg-emerald-100 text-emerald-700"
                                  : "bg-slate-100 text-slate-700";

                              if (!contentTypes[typeName]) {
                                contentTypes[typeName] = {
                                  total: 0,
                                  completed: 0,
                                  icon,
                                  color,
                                };
                              }

                              contentTypes[typeName].total++;

                              if (content.tracking?.status === "COMPLETED") {
                                contentTypes[typeName].completed++;
                              }
                            });

                            const totalCount = section?.contents?.length || 0;
                            const totalCompleted =
                              section?.contents?.filter((content: any) => content.tracking?.status === "COMPLETED").length || 0;

                            if (totalCount === 0) return <div className="text-xs text-gray-400 italic">İçerik yok</div>;

                            const progressPercentage = totalCount > 0 ? Math.round((totalCompleted / totalCount) * 100) : 0;

                            return (
                              <div className="space-y-2">
                                <div className="flex items-center gap-2 mb-2">
                                  <div className="w-48 bg-gray-200 rounded-full h-1.5">
                                    <div
                                      className="bg-green-500 h-1.5 rounded-full transition-all duration-300"
                                      style={{
                                        width: `${progressPercentage}%`,
                                      }}
                                    />
                                  </div>
                                  <span className="text-xs font-medium text-gray-600">%{progressPercentage}</span>
                                </div>
                                <div className="text-xs text-gray-500">
                                  {Object.entries(contentTypes).map(([type, data], index) => (
                                    <span key={type}>
                                      <span className="font-medium">
                                        {data.completed}/{data.total}
                                      </span>{" "}
                                      {type}
                                      {index < Object.entries(contentTypes).length - 1 && ", "}
                                    </span>
                                  ))}
                                  {" (toplam "}
                                  <span className="font-medium">
                                    {totalCompleted}/{totalCount}
                                  </span>
                                  {" içerik)"}
                                </div>
                              </div>
                            );
                          })()}
                        </div>
                      </div>
                    </button>
                    {expandedSections[index as keyof typeof expandedSections] && section?.contents?.length > 0 && (
                      <div className="ml-6 space-y-1">
                        {section?.contents?.map((contentItem: any, lessonIndex: number) => (
                          <div
                            key={lessonIndex}
                            className={`flex items-center justify-between p-2 rounded cursor-pointer transition-colors ${
                              currentContent?.id === contentItem.id ? "bg-purple-50 text-purple-600" : "hover:bg-gray-50"
                            }`}
                            onClick={() => {
                              setIsPlaying(false);
                              setCurrentTime(0);
                              goToContent(section.id, contentItem.id);
                            }}>
                            <div className="flex items-center">
                              <div className="mr-2 flex items-center justify-center flex-shrink-0">{getProgressIcon(contentItem)}</div>
                              <div
                                className={`w-7 h-7 rounded-md mr-2 flex items-center justify-center flex-shrink-0 ${getContentTypeBadgeClass(
                                  contentItem.type || "TEXT"
                                )}`}>
                                {getContentTypeIcon(contentItem.type || "TEXT")}
                              </div>
                              <span className="text-sm line-clamp-1 sm:line-clamp-none min-w-0 flex-1">{contentItem.title}</span>
                            </div>
                            <span className="text-xs text-gray-500">{contentItem.duration}</span>
                          </div>
                        ))}
                        
                        {/* Eksiğini Gider Butonu - Bölümdeki tüm testler tamamlandıysa ve eksik kazanım varsa */}
                        {isSectionTestsCompleted(section) && (sectionGains[section.id]?.length || 0) > 0 && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              void openTutorForGains(sectionGains[section.id] || [], section.id);
                            }}
                            disabled={tutorStarting === section.id}
                            className="w-full mt-3 inline-flex items-center justify-center gap-2 px-4 py-2 rounded-md text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 border border-purple-600 transition-colors disabled:opacity-70 disabled:cursor-not-allowed"
                          >
                            {tutorStarting === section.id ? (
                              <>
                                <Brain className="w-4 h-4 animate-pulse" />
                                <span>Ders planı hazırlanıyor...</span>
                              </>
                            ) : (
                              <>
                                <Sparkles className="w-4 h-4" />
                                <span>AI ile Eksiklerini Gider ({sectionGains[section.id]?.length})</span>
                              </>
                            )}
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {!isSidebarOpen && (
          /* Telefonda SOLDA: sağ altta dururken testin "AI Yardım" düğmesinin üstüne
             biniyordu (sol alt köşede kontrol yok). Masaüstünde sağda kalır. */
          <button
            onClick={() => setIsSidebarOpen(true)}
            className="fixed bottom-4 left-4 md:bottom-6 md:left-auto md:right-6 bg-purple-600 text-white p-3 rounded-full shadow-lg hover:bg-purple-700 transition-all duration-200 z-10 flex items-center gap-2"
            title="Ders İçeriğini Göster">
            <ChevronUp className="w-5 h-5" />
            <span className="hidden sm:inline text-sm font-medium">İçerik</span>
          </button>
        )}
      </div>

      {/* AI Öğretmen paneli global: Layouts.jsx'teki AITutor, toggleAITutorPanel ile açılır */}
    </div>
  );
};
